import React from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Box, Button, Link } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import type { OfferRoomType } from '~/hooks/apis/reservation/useOfferRoomTypes';

const sharedOpts: Partial<GridColDef<OfferRoomType>> = {
  filterable: false,
  hideable: false,
  sortable: false,
  disableColumnMenu: true,
};

function getColumns({
  onOpenAvailability,
}: {
  onOpenAvailability: (roomTypeId: string) => void;
}): Array<GridColDef<OfferRoomType>> {
  return [
    {
      field: 'name',
      headerName: 'Room Name',
      flex: 1.5,
      ...sharedOpts,
    },
    {
      field: 'roomTypeCode',
      headerName: 'Room Code',
      flex: 1,
      valueGetter: (_value, row) => row.roomTypeCode || '-',
      ...sharedOpts,
    },
    {
      field: 'linkedOffers',
      headerName: 'Linked Offers',
      flex: 2,
      renderCell: ({ row }) =>
        row.linkedOffers.length ? (
          <Box component="ul" sx={{ py: 1, my: 0, pl: 2.5 }}>
            {row.linkedOffers.map((linkedOffer) => (
              <li key={linkedOffer.id}>
                <Link component={RouterLink} to={`/offers/${linkedOffer.id}`} target="_blank" underline="hover">
                  {linkedOffer.name}
                </Link>
              </li>
            ))}
          </Box>
        ) : (
          '-'
        ),
      ...sharedOpts,
    },
    {
      field: 'linkedOfferIds',
      headerName: 'Linked Offer IDs',
      flex: 1.5,
      renderCell: ({ row }) =>
        row.linkedOffers.length ? (
          <Box component="ul" sx={{ py: 1, my: 0, pl: 2.5 }}>
            {row.linkedOffers.map((linkedOffer) => (
              <li key={linkedOffer.id}>{linkedOffer.id}</li>
            ))}
          </Box>
        ) : (
          '-'
        ),
      ...sharedOpts,
    },
    {
      field: 'roomGroup',
      headerName: 'Room Group',
      flex: 1,
      valueGetter: (_value, row) => row.roomGroup || '-',
      ...sharedOpts,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.6,
      minWidth: 190,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) =>
        row.defaultRoomRateId ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenAvailability(row.id)}
            startIcon={<CalendarMonthOutlinedIcon fontSize="small" />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Availability & rates
          </Button>
        ) : (
          'No room rates available'
        ),
      ...sharedOpts,
    },
  ];
}

interface Props {
  roomTypes: Array<OfferRoomType>;
  loading?: boolean;
  onOpenAvailability: (roomTypeId: string) => void;
}

export default function RoomTypesTable({ roomTypes, loading, onOpenAvailability }: Props) {
  return (
    <DataGrid
      autoHeight
      disableRowSelectionOnClick
      columns={getColumns({ onOpenAvailability })}
      loading={loading}
      rows={roomTypes}
      getRowHeight={() => 'auto'}
      initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
      pageSizeOptions={[25, 50, 100]}
      sx={{ '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' } }}
    />
  );
}
